import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon} from '@primer/octicons-react'
import {ActionList, Link, Spinner} from '@primer/react'
import {Blankslate, Dialog} from '@primer/react/experimental'
import {memo, useCallback, useEffect, useRef, useState} from 'react'

import type {IssueField} from '../api/common-contracts'
import {checkFieldCollision} from '../helpers/field-rename-helper'
import {getInitialState} from '../helpers/initial-state'
import {useViews} from '../hooks/use-views'
import type {ColumnModel} from '../models/column-model'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import type {RenameState} from '../state-providers/modals/add-field-modal-state-provider'
import styles from './add-issue-fields-dialog.module.css'
import {AddIssueFieldsDialogBanner} from './add-issue-fields-dialog-banner'
import {getValidationResult, ValidationResult} from './add-issue-fields-dialog-validation'
import {getColumnIcon} from './column-detail-helpers'
import {useAddField} from './react_table/hooks/use-add-field'

type AddIssueFieldsDialogProps = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  issueFields: Array<IssueField>
  openAddColumnDialog: () => void
  onSave: (newCols: Array<ColumnModel>) => void
  issueFieldsLoading: boolean
  onSelectFieldWithCollision: (renameState: RenameState) => void
}

const IssueFieldsLoadingSpinner = () => (
  <div role="status" className={styles.loadingStateContainer}>
    <Spinner size="large" />
    <span className="sr-only">Loading...</span>
  </div>
)

export const AddIssueFieldsDialog = memo<AddIssueFieldsDialogProps>(function AddIssueFieldsDialog({
  isOpen,
  setOpen,
  issueFields,
  openAddColumnDialog,
  onSave,
  issueFieldsLoading,
  onSelectFieldWithCollision,
}) {
  const {importIssueFields} = useAddField()
  const {currentView} = useViews()
  const {allColumns, allColumnsRef} = useAllColumns()
  const [visibleIssueFields, setVisibleIssueFields] = useState([...issueFields])

  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(() => new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasImportError, setHasImportError] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const selectedFieldIdsRef = useRef(selectedFieldIds)
  useEffect(() => {
    selectedFieldIdsRef.current = selectedFieldIds
  }, [selectedFieldIds])

  const projectFieldCount = allColumns?.length || 0
  const selectedCount = selectedFieldIds.size
  const {projectOwner} = getInitialState()
  const ownerName = projectOwner?.name?.toLowerCase()

  const validationResult = getValidationResult(selectedCount, projectFieldCount)
  const canSubmit = validationResult === ValidationResult.Valid && !isSubmitting

  useEffect(() => {
    if (!isOpen || isSubmitting || issueFieldsLoading) return

    // Otherwise, refresh the visible issue fields list
    setVisibleIssueFields([...issueFields])
  }, [isOpen, isSubmitting, issueFields, issueFieldsLoading, openAddColumnDialog])

  useEffect(() => {
    if (isOpen) {
      if (selectedFieldIds.size === 0) {
        setSelectedFieldIds(new Set())
      }
      setHasImportError(false)
      setHasAttemptedSubmit(false)
    }
  }, [isOpen, selectedFieldIds.size])

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true)

    if (!canSubmit || !currentView) return

    setHasImportError(false)
    setIsSubmitting(true)

    try {
      await importIssueFields.perform({
        issueFieldIds: Array.from(selectedFieldIds),
        viewNumber: currentView.number,
      })
      if (importIssueFields.status.current.status === 'succeeded') {
        onSave(importIssueFields.status.current.data ?? [])
        setSelectedFieldIds(new Set())
        setOpen(false)
      } else {
        setHasImportError(true)
        setIsSubmitting(false)
      }
    } catch {
      setHasImportError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFieldToggle = useCallback(
    (field: IssueField) => {
      const currentSelectedIds = selectedFieldIdsRef.current
      const isCurrentlySelected = currentSelectedIds.has(field.id)

      if (!isCurrentlySelected) {
        const collision = checkFieldCollision(field, allColumnsRef.current)
        if (collision) {
          const newSelectedIds = new Set(currentSelectedIds)
          newSelectedIds.add(field.id)
          setSelectedFieldIds(newSelectedIds)

          onSelectFieldWithCollision({
            column: collision,
            issueFieldId: field.id,
            source: 'issue-fields-dialog',
            onRenameConfirm: async () => {},
            onRenameCancel: () => {
              setSelectedFieldIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(field.id)
                return newSet
              })
            },
          })
          return
        }
      }

      setSelectedFieldIds(prev => {
        const newSet = new Set(prev)
        if (newSet.has(field.id)) {
          newSet.delete(field.id)
        } else {
          newSet.add(field.id)
        }
        return newSet
      })
    },
    [allColumnsRef, onSelectFieldWithCollision],
  )

  const handleCancel = () => {
    setSelectedFieldIds(new Set())
    setHasImportError(false)
    setHasAttemptedSubmit(false)
    setOpen(false)
  }

  const handleCreateProjectField = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    openAddColumnDialog()
  }

  if (!isOpen) return null

  const showBanners = !isSubmitting || hasImportError
  const hasIssueFields = visibleIssueFields.length > 0

  const footerButtons = issueFieldsLoading
    ? []
    : hasIssueFields
      ? [
          {buttonType: 'default' as const, content: 'Cancel', onClick: handleCancel},
          {
            buttonType: 'primary' as const,
            content: 'Add',
            onClick: handleSubmit,
            loading: isSubmitting,
            ...testIdProps('add-issue-fields-submit-button'),
          },
        ]
      : [{buttonType: 'default' as const, content: 'Cancel', onClick: handleCancel}]

  return (
    <Dialog
      className={styles.dialog}
      title="Add issue fields"
      subtitle={
        <>
          <p>
            Keep your work consistent with issue fields defined by {ownerName}. Some project fields may need renaming.
          </p>
          Need something project specific?{' '}
          <Link as="button" inline onClick={handleCreateProjectField} {...testIdProps('create-project-field-link')}>
            Create a project field
          </Link>
        </>
      }
      onClose={handleCancel}
      height="auto"
      width="large"
      {...testIdProps('add-issue-fields-dialog')}
      footerButtons={footerButtons}
      renderBody={() => (
        <>
          {issueFieldsLoading ? (
            <IssueFieldsLoadingSpinner />
          ) : (
            <>
              {showBanners && (
                <AddIssueFieldsDialogBanner
                  projectCount={projectFieldCount}
                  selectedCount={selectedCount}
                  hasAttemptedSubmit={hasAttemptedSubmit}
                  hasImportError={hasImportError}
                  onRetry={handleSubmit}
                />
              )}
              {visibleIssueFields.length > 0 ? (
                <ActionList
                  className={styles.dialogBody}
                  showDividers
                  selectionVariant="multiple"
                  role="listbox"
                  {...testIdProps('add-issue-fields-list')}
                >
                  {visibleIssueFields.map(field => {
                    const Icon = getColumnIcon(field.dataType)
                    const isSelected = selectedFieldIds.has(field.id)

                    return (
                      <ActionList.Item
                        key={field.id}
                        role="option"
                        aria-selected={isSelected}
                        selected={isSelected}
                        onSelect={e => {
                          e.stopPropagation()
                          handleFieldToggle(field)
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <Icon />
                        </ActionList.LeadingVisual>
                        {field.name}
                      </ActionList.Item>
                    )
                  })}
                </ActionList>
              ) : (
                <Blankslate size="small" className={styles.emptyState} {...testIdProps('add-issue-fields-empty-state')}>
                  <Blankslate.Visual>
                    <BookIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>No available issue fields</Blankslate.Heading>
                  <Blankslate.Description>
                    Ask your organization admin to add issue fields, or{' '}
                    <Link as="button" inline onClick={handleCreateProjectField}>
                      create a local project field
                    </Link>
                    .
                  </Blankslate.Description>
                </Blankslate>
              )}
            </>
          )}
        </>
      )}
    />
  )
})

try{ IssueFieldsLoadingSpinner.displayName ||= 'IssueFieldsLoadingSpinner' } catch {}
try{ AddIssueFieldsDialog.displayName ||= 'AddIssueFieldsDialog' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}